import React, { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { ArrowLeft } from 'lucide-react';
import PackageStepIndicator from '@/components/PackageStepIndicator';
import {
  getPackage,
  getStepIndex,
  PACKAGE_STEPS,
  getNextStep,
  getPrevStep,
  getStepPath,
  getLevelStyles,
} from '@/data/packageData';

const LOGO_URL = '/images/TCA_Logo.png';
const BANNER_URL = '/images/TCA_Banner.png';
const TCA_SHORT_LOGO = '/images/TCA_Short_Logo.png';

const StepPlaceholder = ({ stepKey }) => {
  const { packageSlug } = useParams();
  const navigate = useNavigate();

  const pkg = getPackage(packageSlug);
  const stepIndex = getStepIndex(stepKey);
  const stepInfo = PACKAGE_STEPS[stepIndex];
  const prevStep = getPrevStep(stepKey);
  const nextStep = getNextStep(stepKey);

  useEffect(() => {
    if (!pkg) navigate('/', { replace: true });
    window.scrollTo(0, 0);
  }, [pkg, navigate]);

  if (!pkg) return null;

  const levelStyles = getLevelStyles(pkg.colorKey);

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <main className="flex-1 flex flex-col items-center px-4 pt-[2px] pb-8 sm:pb-12 bg-white">
        {/* Back link removed from top — now below heading */}

        {/* Logo */}
        <div className="mb-6 sm:mb-8 flex flex-col items-center gap-4">
          <img
            src={LOGO_URL}
            alt="TCA Auditors Logo"
            className="h-24 sm:h-32 w-auto object-contain select-none"
            draggable="false"
          />
          <img
            src={BANNER_URL}
            alt="Tax & Auditing Consultants - For Businesses & Individuals"
            className="h-[4.5rem] sm:h-24 w-auto object-contain"
          />
        </div>

        {/* Heading */}
        <div className="mb-4 pb-2">
          <h1 className="text-3xl sm:text-4xl font-extrabold text-tca-blue text-center tracking-wide inline-block underline-tca-blue">
            ACCOUNTING RETAINER AGREEMENT
          </h1>
        </div>

        {/* Top back button — matches Comparison Table style */}
        <div className="w-full max-w-2xl flex justify-start mb-6">
          <Button
            onClick={() => navigate('/')}
            className="bg-accent hover:bg-accent/90 text-white font-semibold py-2 px-6 h-9 text-sm rounded-lg shadow-soft hover:shadow-card transition-all duration-200 flex items-center gap-2"
            data-testid="back-to-welcome-top"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Welcome Screen
          </Button>
        </div>

        <Card className="w-full max-w-2xl border-0 shadow-card">
          <CardContent className="p-6 sm:p-10 flex flex-col items-center text-center space-y-6">
            {/* Package badge */}
            <span
              className="text-sm font-bold px-4 py-1.5 rounded-full"
              style={levelStyles}
              data-testid="package-badge"
            >
              {pkg.fullName}
            </span>

            <h2 className="text-2xl font-bold text-tca-blue">
              {stepInfo?.label}
            </h2>

            <p className="text-sm text-muted-foreground italic">
              This section is currently being prepared and will be available shortly.
            </p>

            {/* Navigation */}
            <div className="flex gap-4 justify-center pt-4 flex-wrap">
              {prevStep && (
                <Button
                  onClick={() => navigate(getStepPath(packageSlug, prevStep.key))}
                  data-testid="btn-prev-step"
                  className="bg-accent hover:bg-accent/90 text-white font-semibold py-2 px-6 h-9 text-sm rounded-lg shadow-soft hover:shadow-card transition-all duration-200 flex items-center gap-2"
                >
                  <ArrowLeft className="w-4 h-4" />
                  Back to {prevStep.label}
                </Button>
              )}
              {nextStep ? (
                <Button
                  onClick={() => navigate(getStepPath(packageSlug, nextStep.key))}
                  data-testid="btn-next-step"
                  className="bg-accent hover:bg-accent/90 text-accent-foreground font-semibold"
                >
                  Go to the Next Page &rarr;
                </Button>
              ) : (
                <Button
                  onClick={() => { /* Final submission handler — to be wired up when backend is ready */ }}
                  data-testid="btn-finalise-send"
                  className="bg-accent hover:bg-accent/90 text-accent-foreground font-semibold"
                >
                  Finalise and Send &rarr;
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Bottom back button */}
        <div className="w-full max-w-2xl flex justify-start mt-4 mb-6">
          <Button
            onClick={() => navigate('/')}
            className="bg-accent hover:bg-accent/90 text-white font-semibold py-2 px-6 h-9 text-sm rounded-lg shadow-soft hover:shadow-card transition-all duration-200 flex items-center gap-2"
            data-testid="back-to-welcome-bottom"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Welcome Screen
          </Button>
        </div>
      </main>

      <div className="relative">
        <PackageStepIndicator pkg={pkg} currentStepKey={stepKey} />
        <img
          src={TCA_SHORT_LOGO}
          alt="TCA Logo"
          className="absolute bottom-2 right-56 h-12 w-auto object-contain opacity-80"
        />
      </div>
    </div>
  );
};

export default StepPlaceholder;
